import "bootstrap/dist/css/bootstrap.min.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import './global-styles.css';
import React, { useState, useEffect, Component } from "react";
import styled from "styled-components";
import Login from "./features/auth/Login";
import AuthCallback from "./features/auth/services/AuthCallback";
import ProtectedRoute from "./app/ProtectedRoute";
import { ThemeProvider, useTheme } from "./middleware/ThemeContext";
import { ToastProvider, useToast } from "./components/toast/ToastContext";
import SidebarMenu from "./components/sidebar/SideBarMenu";
import TopBar from "./components/topBar/TopBar";
import ControlBar from "./components/controlBar/controlBar";
import Dashboard from "./features/dashboard/Dashboard";
import Contacts from "./features/contacts/Contacts";
import Organizations from "./features/organizations/Organizations";
import GenericEntityForm from "./features/contacts/GenericEntityForm";
import ContactPreview from "./features/contacts/ContactPreview";
import OrganizationPreview from "./features/organizations/OrganizationPreview";
import FormBuilder from "./components/FormBuilder/CustomForm/FormBuilder";
import FormTemplates from "./features/forms/FormTemplates/FormTemplates";
import CustomForms from "./features/forms/CustomForms/CustomForms";
import QueryFoldersListing from "./features/searchQuery/QueryFoldersListing";
import QueryScopeModal from "./features/searchQuery/QueryScopeModal";
import QueryCreation from "./features/searchQuery/QueryCreation";
import CreateFormModal from "./components/FormBuilder/CustomForm/CreateFormModal";
import FormFiller from "./components/FormBuilder/CustomForm/FormFiller";
import AddContactRoles from "./features/contactRoles/AddContactRoles";
import EditRolePermissions from "./features/contactRoles/EditRolePermissions";
import Roles from "./features/contactRoles/Roles";
import CustomFields from "./features/forms/CustomFields/CustomFields";
import { FieldSettingsModal } from "./components/FormBuilder/FieldSettings/FieldSettingsModal";
import { useCreateCustomFieldTemplateMutation } from "./api/formsApiSlice";
import { DEFAULT_BASIC_CONFIG } from "./types/types";
import Components from "./features/forms/Components/Components";
import CreateComponentModal from "./features/forms/Components/CreateComponentModal";
import { createNewField } from "./components/FormBuilder/FieldSettings/fieldSettingsHelper";
import MergeContacts from "./features/contacts/MergeContacts";
import MergeOrganizations from "./features/organizations/MergeOrganizations";
import ImportCenter from "./features/imports/ImportCenter";
import Records from "./features/imports/Records";
import ImportModal from "./features/imports/Modals/ImportModal";
import FieldMapping from "./features/imports/FieldMapping";
import DuplicateChecker from "./features/duplicateChecker/DuplicateChecker";
import DuplicateContacts from "./features/duplicateChecker/DuplicateContacts";
import Preview from "./features/imports/Preview";
import DuplicateOrganizations from "./features/duplicateChecker/DuplicateOrganizations";
import { set } from "lodash";
import Listing from "./features/Widget/Listing";
import CreateWidgetModal from "./components/FormBuilder/CustomForm/CreateWidget";
import BuilderLandingPage from "./features/websiteBuilder/BuilderLandingPage";
import WebsiteBuilderDefaultTheme from "./features/websiteBuilder/WebsiteBuilderDefaultTheme";
import Builder from "./features/websiteBuilder/WebsiteConfiguration/Builder";
import { useGetSettingDetailsQuery } from "./api/pageBuilderApiSlice";
import WebsiteBuilderRedirect from "./app/WebsiteBuilderRedirect";
import PageBuilder from "./components/PageBuilder";
import PageBuilderNew from "./components/PageBuilder/PageBuilder";
import MembershipDashboard from "./features/memberships/dashboard/MembershipDashboard";
import AddNewMembershipType from "./features/memberships/membershipCreation/AddNewMembershipType";

const HomeContainer = styled.div({
  display: "flex",
  height: "100%",
});

const MainContent = styled(SimpleBar)<{ theme: any }>(({ theme }) => ({
  flexGrow: 1,
  backgroundColor: theme.colors.contentBackground,
  color: theme.colors.contentText,
  minWidth: "600px",
  maxHeight: "100vh",   // enforce height for scrolling
  overflow: "hidden",
}));

const HomeLayout = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useToast();
  const [disableQuery, setDisableQuery] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const hasFormsInPath = location.pathname.includes("forms");
  const hasQueryInPath = location.pathname.includes("query");
  const hasRolesInPath = location.pathname.includes("roles");
  const hasFieldsInPath = location.pathname.includes("fields");


  const [isSubMenuCollapsed, setSubMenuCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarSubMenuCollapsed");
    return saved ? JSON.parse(saved) : false;
  });

  const [showEntityForm, setShowEntityForm] = useState(false);
  const [selectedEntityId, setSelectedEntityId] = useState<number | null>(null);
  const [formType, setFormType] = useState<"contact" | "organization">(
    "contact"
  );
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [appLoaded, setappLoaded] = useState(false);
  const [newQuery, setNewQuery] = useState(false);
  const [addRoles, setAddRoles] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [showWidgetModal, setShowWidgetModal] = useState(false);

  const [showMembershipTypeModal, setShowMembershipTypeModal] = useState(false);

  const [showNewFieldModal, setShowNewFieldModal] = useState(false);
  const [showNewComponentModal, setNewComponentModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState({ state: false, type: '' })
  const [hasUploaded, setHasUploaded] = useState(false);
  const [selectedOption, setSelectedOption] = React.useState('');
  const [createFieldTemplate] = useCreateCustomFieldTemplateMutation();

  useEffect(() => {
    const token = JSON.parse(
      localStorage.getItem("authData") || "{}"
    )?.accessToken;
    if (!token) navigate("/");
  }, [navigate]);

  useEffect(() => {
    if (!appLoaded) {
      setappLoaded(true);
      const currentPath = window.location.pathname;
      if (currentPath === "/home" || currentPath === "/") {
        const selectedSubMenu = localStorage.getItem("selectedSubMenu");

        if (selectedSubMenu) {
          try {
            const subMenu = JSON.parse(selectedSubMenu);
            navigate(subMenu);
          } catch (e) {
            navigate("contacts");
          }
        }
      }
    }
  }, [appLoaded, navigate, setappLoaded]);

  useEffect(() => {
    localStorage.setItem(
      "sidebarSubMenuCollapsed",
      JSON.stringify(isSubMenuCollapsed)
    );
  }, [isSubMenuCollapsed]);

  const handleFormSuccess = () => {
    setRefreshTrigger((prev) => prev + 1);
    setShowEntityForm(false);
    setSelectedEntityId(null);
  };

  return (
    <HomeContainer>
      <SidebarMenu
        onSubMenuItemClick={() => { }}
        isSubMenuCollapsed={isSubMenuCollapsed}
        setSubMenuCollapsed={setSubMenuCollapsed}
      />
      <MainContent theme={theme}>
        <TopBar onMenuToggle={() => setSubMenuCollapsed(!isSubMenuCollapsed)} />
        <ControlBar
          component={location.pathname.includes("components")}
          field={location.pathname.includes("fields")}
          form={location.pathname.includes("forms")}
          query={location.pathname.includes("query")}
          contact={location.pathname.includes("contacts")}
          oranization={location.pathname.includes("organizations")}
          events={location.pathname.includes("events")}
          imports={location.pathname.includes("imports")}
          mapping={location.pathname.includes("mapping")}
          preview={location.pathname.includes("preview")}
          widget={location.pathname.includes("widgets")}
          membership={location.pathname.includes("membership")}
          roles={hasRolesInPath}
          onNewContact={() => {
            setFormType("contact");
            setSelectedEntityId(null);
            setShowEntityForm(true);
          }}
          onNewOrganization={() => {
            setFormType("organization");
            setSelectedEntityId(null);
            setShowEntityForm(true);
          }}
          onNewQuery={() => setNewQuery(true)}
          onNewForm={() => setShowFormModal(true)}
          onAddRoles={() => setAddRoles(true)}
          onNewField={() => setShowNewFieldModal(true)}
          onNewComponent={() => setNewComponentModal(true)}
          onNewWidget={() => setShowWidgetModal(true)}
          onImport={(type: "contact" | "organization" | "relationship") => setShowImportModal({ state: true, type: type })}
          onNewPlan={() => setShowMembershipTypeModal(true)}
        />
        <Outlet
          context={{
            setSelectedEntity: (
              type: "contact" | "organization",
              id: number
            ) => {
              setFormType(type);
              setSelectedEntityId(id);
              setShowEntityForm(true);
            },
            disableQuery,
            setDisableQuery,
            setSelectedColumns,
            setTableData,
            tableData,
            selectedColumns,
            refreshTrigger,
            setRefreshTrigger,
            onEditRole: (id: number) => {
              setSelectedRoleId(id);
              setAddRoles(true);
            },
          }}
        />
        {showEntityForm && (
          <GenericEntityForm
            onClose={() => {
              setShowEntityForm(false);
              setSelectedEntityId(null);
            }}
            onSuccess={handleFormSuccess}
            formType={formType}
          />
        )}
        {newQuery && <QueryScopeModal onClose={() => setNewQuery(false)} setSelectedColumns={setSelectedColumns} setTableData={setTableData}
        />}
        {addRoles &&
          <AddContactRoles
            id={selectedRoleId || undefined}
            onSuccess={() => setRefreshTrigger((prev) => prev + 1)}
            onClose={() => {
              setAddRoles(false); setSelectedRoleId(null);
            }}
          />
        }
        <CreateFormModal
          isOpen={showFormModal}
          onClose={() => setShowFormModal(false)}
          onSuccess={(id) => {
            setShowFormModal(false);
            navigate(`/home/forms/${id}`);
          }}
        />
        <FieldSettingsModal
          config={DEFAULT_BASIC_CONFIG}
          show={showNewFieldModal}
          onHide={() => setShowNewFieldModal(false)}
          // onSave={() => setShowNewFieldModal(false)}
          onSaveForCreate={(
            fieldName,
            fieldDataType,
            fieldScope,
            config,
            accessKey,
          ) => {
            return createNewField(
              fieldName,
              fieldDataType,
              fieldScope,
              config,
              accessKey,
              createFieldTemplate,
              setRefreshTrigger,
              showToast,
              () => setShowNewFieldModal(false)
            );
          }}

          isCreating
        />
        <CreateComponentModal
          show={showNewComponentModal}
          onHide={() => setNewComponentModal(false)}
          onSave={() => setRefreshTrigger((prev) => prev + 1)}
        />
        <CreateWidgetModal
          isOpen={showWidgetModal}
          onClose={() => setShowWidgetModal(false)}
          onSuccess={() => {
            setShowWidgetModal(false);
            navigate(`/home/widgets`);
          }}
        />
        <ImportModal setHasUploaded={setHasUploaded} hasUploaded={hasUploaded} selectedOption={selectedOption} isOpen={showImportModal.state} scope={showImportModal.type} onClose={() => { setShowImportModal({ state: false, type: '' }); setSelectedOption(''); setHasUploaded(false) }} setSelectedOption={setSelectedOption} />

        {showMembershipTypeModal &&
          <AddNewMembershipType
            onClose={() => setShowMembershipTypeModal(false)}
          />
        }
      </MainContent>
    </HomeContainer>
  );
};

const App = () => {
  return (

    <ToastProvider>
      <ThemeProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/auth/cb" element={<AuthCallback />} />
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <HomeLayout />
                </ProtectedRoute>
              }
            >
              <Route path="comingSoon" element={<Dashboard />} />
              <Route path="/home/contacts/:id" element={<ContactPreview />} />
              <Route
                path="/home/contacts/:id/notes"
                element={<ContactPreview />}
              />
              <Route
                path="/home/contacts/:id/certification"
                element={<ContactPreview />}
              />
              <Route
                path="/home/contacts/:id/communication"
                element={<ContactPreview />}
              />
              <Route
                path="/home/contacts/:id/courses"
                element={<ContactPreview />}
              />
              <Route
                path="/home/contacts/:id/finance"
                element={<ContactPreview />}
              />
              <Route
                path="/home/contacts/:id/memberships"
                element={<ContactPreview />}
              />
              <Route
                path="/home/contacts/merge/"
                element={<MergeContacts />}
              />
              <Route
                path="/home/organizations/merge/"
                element={<MergeOrganizations />}
              />

              <Route
                path="/home/organizations/:id"
                element={<OrganizationPreview />}
              />
              <Route
                path="/home/organizations/:id/pending-join-requests"
                element={<OrganizationPreview />}
              />
              <Route
                path="/home/duplicate-checker"
                element={<DuplicateChecker />}
              />
              <Route
                path="/home/duplicate-checker/contacts"
                element={<DuplicateContacts />}
              />
              <Route
                path="/home/duplicate-checker/organization"
                element={<DuplicateOrganizations />}
              />
              <Route path="contacts" element={<Contacts />} />
              <Route path="organizations" element={<Organizations />} />
              <Route path="forms" element={<CustomForms />} />
              <Route path="widgets" element={<Listing />} />
              <Route path="fields" element={<CustomFields />} />
              <Route path="components" element={<Components />} />
              <Route path="search-query" element={<QueryFoldersListing />} />
              <Route path="contact-roles" element={<Roles />} />
              <Route path="/home/roles-permissions/:id?" element={<EditRolePermissions />} />

              {/* Import Routes */}
              <Route path="imports" element={<ImportCenter />} />
              <Route path="/home/records/:id" element={<Records />} />
              <Route path="/home/mapping/:id/:scopeId" element={<FieldMapping />} />
              <Route path="/home/preview" element={<Preview />} />


              {/* Import Routes */}

              <Route
                path="/home/search-query/query-preview"
                element={<QueryCreation />}
              />
              <Route
                path="/home/search-query/query-preview/:queryId"
                element={<QueryCreation />}
              />
              <Route path="/home/forms/:id" element={<FormBuilder />} />
              <Route path="/home/website-builder" element={<WebsiteBuilderRedirect />} />
              <Route path="/home/web-builder-default" element={<WebsiteBuilderDefaultTheme />} />
              <Route path="/home/builder" element={<Builder />} />
              <Route path="/home/page-builder/:id" element={<PageBuilderNew />} />

              {/* Membership Routes */}

              <Route path="/home/membership-dashboard" element={<MembershipDashboard />} />

            </Route>
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </ToastProvider>
  );
};

export default App;
